import React, { useState, useLayoutEffect } from 'react';
import { measureNode } from '../utils.js';
import { resolveNode } from '../utils/responsive.js';

const GizmoOverlay = ({
  activeNodes,
  selectedNodeIds,
  rootW,
  rootH,
  onResizeMouseDown,
  onAxisDragMouseDown,
  canvasRef,
  canvasScale,
  activeBreakpoint = 'default'
}) => {
  const [rects, setRects] = useState({});

  const measure = React.useCallback(() => {
    const newRects = {};

    selectedNodeIds.forEach(id => {
      const rawNode = activeNodes.find(n => n.id === id);
      if (!rawNode) return;

      const node = resolveNode(rawNode, activeBreakpoint);

      const el = document.getElementById(node.id);
      if (el && canvasRef.current) {
        const elRect = el.getBoundingClientRect();
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const borderLeft = canvasRef.current.clientLeft || 0;
        const borderTop = canvasRef.current.clientTop || 0;

        newRects[id] = {
          left: (elRect.left - canvasRect.left - borderLeft) / canvasScale,
          top: (elRect.top - canvasRect.top - borderTop) / canvasScale,
          width: elRect.width / canvasScale,
          height: elRect.height / canvasScale,
          position: 'absolute'
        };
      } else {
        // Fallback to theoretical using resolved node logic
        const rect = measureNode(node.id, activeNodes, rootW, rootH, activeBreakpoint);
        newRects[id] = {
          left: rect.x,
          top: rect.y,
          width: rect.width,
          height: rect.height,
          position: 'absolute'
        };
      }
    });

    setRects(prev => {
      // Deep compare to avoid unnecessary re-renders
      let hasChanged = false;
      if (Object.keys(newRects).length !== Object.keys(prev).length) {
        hasChanged = true;
      } else {
        for (const id of Object.keys(newRects)) {
          const oldRect = prev[id];
          const newRect = newRects[id];
          if (!oldRect ||
              Math.abs(oldRect.left - newRect.left) > 0.1 ||
              Math.abs(oldRect.top - newRect.top) > 0.1 ||
              Math.abs(oldRect.width - newRect.width) > 0.1 ||
              Math.abs(oldRect.height - newRect.height) > 0.1) {
            hasChanged = true;
            break;
          }
        }
      }
      return hasChanged ? newRects : prev;
    });
  }, [selectedNodeIds, activeNodes, canvasRef, canvasScale, activeBreakpoint, rootW, rootH]);

  useLayoutEffect(() => {
    measure();

    // Set up ResizeObserver for all selected nodes to catch size changes from ContentSizeFitter etc.
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        measure();
      });

      selectedNodeIds.forEach(id => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });

      return () => observer.disconnect();
    }
  }, [measure, selectedNodeIds]); 

  return (
    <div className="absolute inset-0 pointer-events-none z-[100]">
      {selectedNodeIds.map(id => {
        const rawNode = activeNodes.find(n => n.id === id);
        if (!rawNode) return null;
        
        const node = resolveNode(rawNode, activeBreakpoint);

        // Check if disabled - don't show gizmo for disabled nodes
        const isEnabled = node.props?.enabled !== false;
        if (!isEnabled) return null;

        // Visibility Check (edit mode only)
        let isVisible = true;
        if (node.props?.visibility !== undefined) {
             if (typeof node.props.visibility === 'boolean') {
                  isVisible = node.props.visibility;
             } else if (activeBreakpoint && typeof node.props.visibility === 'object') {
                  if (node.props.visibility[activeBreakpoint] === false) {
                       isVisible = false;
                  }
             }
        }

        if (!isVisible) return null;

        const isLocked = node.props.locked;

        // Use measured rect or fallback to 0/hidden if not ready (to prevent jumping)
        const style = rects[id] || { display: 'none' };

        return (
          <div key={id} style={style} className="pointer-events-none">
            {/* Selection Outline */}
            <div
              className={`absolute inset-0 border-2 ${
                isLocked ? 'border-red-500' : 'border-blue-500'
              } pointer-events-none`}
            />

            {!isLocked && (
              <>
                {/* Resize Handles */}
                <div
                  className="absolute top-0 left-0 w-2 h-2 bg-white border border-blue-500 cursor-nw-resize -translate-x-1 -translate-y-1 pointer-events-auto"
                  onMouseDown={(e) => onResizeMouseDown(e, node.id, 'nw')}
                />
                <div
                  className="absolute top-0 right-0 w-2 h-2 bg-white border border-blue-500 cursor-ne-resize translate-x-1 -translate-y-1 pointer-events-auto"
                  onMouseDown={(e) => onResizeMouseDown(e, node.id, 'ne')}
                />
                <div
                  className="absolute bottom-0 left-0 w-2 h-2 bg-white border border-blue-500 cursor-sw-resize -translate-x-1 translate-y-1 pointer-events-auto"
                  onMouseDown={(e) => onResizeMouseDown(e, node.id, 'sw')}
                />
                <div
                  className="absolute bottom-0 right-0 w-2 h-2 bg-white border border-blue-500 cursor-se-resize translate-x-1 translate-y-1 pointer-events-auto"
                  onMouseDown={(e) => onResizeMouseDown(e, node.id, 'se')}
                />

                <div
                  className="absolute top-0 left-1/2 w-2 h-2 bg-white border border-blue-500 cursor-n-resize -translate-x-1/2 -translate-y-1 pointer-events-auto"
                  onMouseDown={(e) => onResizeMouseDown(e, node.id, 'n')}
                />
                <div
                  className="absolute bottom-0 left-1/2 w-2 h-2 bg-white border border-blue-500 cursor-s-resize -translate-x-1/2 translate-y-1 pointer-events-auto"
                  onMouseDown={(e) => onResizeMouseDown(e, node.id, 's')}
                />
                <div
                  className="absolute left-0 top-1/2 w-2 h-2 bg-white border border-blue-500 cursor-w-resize -translate-x-1 -translate-y-1/2 pointer-events-auto"
                  onMouseDown={(e) => onResizeMouseDown(e, node.id, 'w')}
                />
                <div
                  className="absolute right-0 top-1/2 w-2 h-2 bg-white border border-blue-500 cursor-e-resize translate-x-1 -translate-y-1/2 pointer-events-auto"
                  onMouseDown={(e) => onResizeMouseDown(e, node.id, 'e')}
                />

                {/* Center Pivot */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white border border-gray-500 rounded-full z-[60] pointer-events-none" />

                {/* X Axis Arrow (Red) */}
                <div
                  className="absolute left-1/2 top-1/2 flex items-center cursor-e-resize z-[60] group/x-axis pointer-events-auto pl-2"
                  style={{ transform: 'translate(0, -50%)', height: '20px' }}
                  onMouseDown={(e) => onAxisDragMouseDown(e, node.id, 'x')}
                >
                  <div className="w-12 h-1 bg-red-500 relative transition-all shadow-sm group-hover/x-axis:h-1.5">
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[12px] border-l-red-500" />
                  </div>
                </div>

                {/* Y Axis Arrow (Green) */}
                <div
                  className="absolute left-1/2 top-1/2 flex flex-col items-center cursor-s-resize z-[60] group/y-axis pointer-events-auto pt-2"
                  style={{ transform: 'translate(-50%, 0)', width: '20px' }}
                  onMouseDown={(e) => onAxisDragMouseDown(e, node.id, 'y')}
                >
                  <div className="h-12 w-1 bg-green-500 relative transition-all shadow-sm group-hover/y-axis:w-1.5">
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[12px] border-t-green-500" />
                  </div>
                </div>

                {/* Size Label (Lower Right Corner Inside) */}
                <div
                  className="absolute bottom-1 right-1 px-2 py-1 bg-blue-500 text-white text-xs font-mono rounded shadow-lg pointer-events-none whitespace-nowrap"
                >
                  {Number(style.width).toFixed(1).replace(/\.0$/, '')} × {Number(style.height).toFixed(1).replace(/\.0$/, '')}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GizmoOverlay;
